import { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Typography } from '@mui/material'
import { isNil } from 'es-toolkit'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { api } from '@/api'
import { AuthErrorCode, TwoFactorAuthType, TwoFactorEnforcementState } from '@/api/types'
import { Button } from '@/components/ui/button'
import { ChevronRightIcon } from '@/components/ui/icons'
import { PhoneField } from '@/components/ui/phone-field'
import { authService } from '@/services/auth-service'
import { isErrorCode } from '@/utils/error'
import { maskPhone } from '@/utils/string'

import { EmailSignInNotice } from './email-sign-in-notice'
import { MfaMethodPicker } from './mfa-method-picker'

interface Props {
  allowedMethods: TwoFactorAuthType[]
  defaultMethod: TwoFactorAuthType
  // Full-session callers load the live method details instead of reading the login token.
  allowRetrieveAuthDetails: boolean
  // Proving an existing factor offers that factor; only a new enrolment is limited to what the company allows.
  provingExistingMethod: boolean
  allowEditPhoneNumber: boolean
  // Email still works as a sign-in factor until the enforcement date; reset and step-up never take it.
  allowEmailMethod: boolean
  // Enforcement is off and the company still takes email: offer it as the default next to text and call.
  offerEmailChoice: boolean
  sendCodeMessageText: string
  sendCodeButtonText: string
  onCodeSent: (method: TwoFactorAuthType, otpSentTo?: string | null) => void
  onEnrollAuthenticator?: VoidFunction
  onEmailRetired?: VoidFunction
}

function isPhoneMethod(method: TwoFactorAuthType) {
  return method === TwoFactorAuthType.Sms || method === TwoFactorAuthType.Call
}

// Email is chosen only when the caller offers it; otherwise a user who has it keeps it until the enforcement date.
function isSelectableMethod(method: TwoFactorAuthType, offerEmailChoice: boolean) {
  return (
    isPhoneMethod(method) ||
    method === TwoFactorAuthType.Authenticator ||
    (offerEmailChoice && method === TwoFactorAuthType.Email)
  )
}

const formSchema = z.object({
  twoFactorType: z.preprocess(Number, z.nativeEnum(TwoFactorAuthType)),
  phoneNumber: z.string().nullish(),
})

type FormData = z.infer<typeof formSchema>

// Keep the user's own method when it is still offered, else fall back to the first one that is.
function pickMethod(preferred: TwoFactorAuthType | null | undefined, allowedMethods: TwoFactorAuthType[]) {
  if (!isNil(preferred) && allowedMethods.includes(preferred)) return preferred
  return allowedMethods[0] ?? TwoFactorAuthType.Sms
}

export function SendMFACodeForm({
  allowedMethods,
  defaultMethod,
  allowRetrieveAuthDetails,
  provingExistingMethod,
  allowEditPhoneNumber,
  allowEmailMethod,
  offerEmailChoice,
  sendCodeMessageText,
  sendCodeButtonText,
  onCodeSent,
  onEnrollAuthenticator,
  onEmailRetired,
}: Props) {
  const decodedToken = authService.getDecodedToken()

  const details = api.useGetTwoFactorAuthDetails({ query: { enabled: allowRetrieveAuthDetails } }).data?.data

  // Numbers are masked server-side, so they are shown, never sent back.
  const phoneOnFile = (allowRetrieveAuthDetails ? details?.phoneNumber : decodedToken?.PhoneNumber) || ''
  const maskedEmail = decodedToken?.Email
  // Full session tokens carry no enforcement date, so both values come from the live details when those are loaded.
  const enforcementDate = allowRetrieveAuthDetails ? details?.mfaEnforcementDate : decodedToken?.MfaEnforcementDate
  const enforcementState = allowRetrieveAuthDetails ? details?.mfaEnforcementState : decodedToken?.MfaEnforcementState
  const offeredMethods: TwoFactorAuthType[] = provingExistingMethod
    ? allowedMethods
    : (details?.allowedMethods ?? allowedMethods)
  // The app is only offered where the caller can host its enrolment.
  const effectiveAllowedMethods = offeredMethods
    .filter((method) => isSelectableMethod(method, offerEmailChoice))
    .filter((method) => method !== TwoFactorAuthType.Authenticator || !!onEnrollAuthenticator)
  const emailMethod = allowEmailMethod ? (details?.authType ?? defaultMethod) : null
  // The user proves the email method they already have: no choice to make, just send the code.
  const provingEmail =
    allowEmailMethod && effectiveAllowedMethods.length === 0 && offeredMethods.includes(TwoFactorAuthType.Email)

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      twoFactorType: pickMethod(defaultMethod, effectiveAllowedMethods),
      phoneNumber: '',
    },
  })

  const twoFactorType = useWatch({ control: form.control, name: 'twoFactorType' })
  const isAuthenticator = twoFactorType === TwoFactorAuthType.Authenticator
  const isEmail = twoFactorType === TwoFactorAuthType.Email
  const usesPhone = !isAuthenticator && !isEmail

  const sendCode = api.useSendTwoFactorAuthCode({
    mutation: {
      onSuccess: (_, variables) =>
        onCodeSent(
          variables.data.twoFactorAuthType,
          variables.data.twoFactorAuthType === TwoFactorAuthType.Email
            ? maskedEmail
            : variables.data.phoneNumber
              ? maskPhone(variables.data.phoneNumber)
              : phoneOnFile,
        ),
      onError: (error) => {
        if (isErrorCode(error, AuthErrorCode.EmailRetired)) onEmailRetired?.()
      },
    },
  })

  useEffect(() => {
    if (!details) return
    form.reset({ twoFactorType: pickMethod(details.authType, effectiveAllowedMethods), phoneNumber: '' })
    // Reset once per details load; the allowed list is derived from the same payload.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [details, form])

  const submit = (data: FormData) => {
    if (data.twoFactorType === TwoFactorAuthType.Authenticator) {
      onEnrollAuthenticator?.()
      return
    }

    if (data.twoFactorType === TwoFactorAuthType.Email) {
      sendCode.mutate({ data: { twoFactorAuthType: TwoFactorAuthType.Email, phoneNumber: null } })
      return
    }

    const phoneNumber = data.phoneNumber?.trim() || null
    // Without an editable field the server decides; it answers MethodNotSetUp when nothing is on file.
    if (!phoneNumber && !phoneOnFile && allowEditPhoneNumber) {
      form.setError('phoneNumber', { message: 'Phone number is required' })
      return
    }

    sendCode.mutate({ data: { twoFactorAuthType: data.twoFactorType, phoneNumber } })
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      form.handleSubmit(submit)()
    }
  }

  if (provingEmail) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {enforcementState !== TwoFactorEnforcementState.Off && <EmailSignInNotice enforcementDate={enforcementDate} />}
        {maskedEmail && <Typography variant="body2">We will send the code to {maskedEmail}.</Typography>}
        <Button
          loading={sendCode.isPending}
          color="primary"
          variant="contained"
          size="large"
          fullWidth
          endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
          onClick={() => sendCode.mutate({ data: { twoFactorAuthType: TwoFactorAuthType.Email, phoneNumber: null } })}
        >
          {sendCodeButtonText}
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Controller
        name="twoFactorType"
        control={form.control}
        render={({ field }) => (
          <MfaMethodPicker
            label={sendCodeMessageText}
            allowedMethods={effectiveAllowedMethods}
            value={field.value}
            onChange={field.onChange}
            currentMethod={emailMethod}
            currentEmail={maskedEmail}
            enforcementDate={enforcementDate}
            enforcementState={enforcementState}
          />
        )}
      />
      {usesPhone && allowEditPhoneNumber && (
        <Controller
          name="phoneNumber"
          control={form.control}
          render={({ field }) => (
            <PhoneField
              {...field}
              value={field.value ?? ''}
              label="Phone Number"
              error={!!form.formState.errors.phoneNumber}
              helperText={
                form.formState.errors.phoneNumber?.message ??
                (phoneOnFile ? `Leave blank to keep ${phoneOnFile}` : undefined)
              }
              onKeyDown={handleKeyDown}
            />
          )}
        />
      )}
      {usesPhone && !allowEditPhoneNumber && phoneOnFile && (
        <Typography variant="body2">We will send the code to {phoneOnFile}.</Typography>
      )}
      {isEmail && maskedEmail && <Typography variant="body2">We will send the code to {maskedEmail}.</Typography>}
      <Button
        loading={sendCode.isPending}
        color="primary"
        variant="contained"
        size="large"
        fullWidth
        endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
        onClick={form.handleSubmit(submit)}
      >
        {isAuthenticator ? 'Continue' : sendCodeButtonText}
      </Button>
    </Box>
  )
}
